import { Editor } from "@tiptap/core";
import DiffMatchPatch from "diff-match-patch";

/**
 * Diff operation types from diff-match-patch
 * -1: deletion, 0: unchanged, 1: insertion
 */
export type DiffOperation = -1 | 0 | 1;

export interface DiffChunk {
  operation: DiffOperation;
  text: string;
}

export interface DiffResult {
  chunks: DiffChunk[];
  hasChanges: boolean;
}

/**
 * Represents a range for applying an insertion mark
 */
export interface InsertionRange {
  /** Start position in the current text (before deletions are inserted) */
  textFrom: number;
  /** End position in the current text (before deletions are inserted) */
  textTo: number;
}

/**
 * Represents a deletion to be inserted as a node
 */
export interface DeletionInfo {
  /** Position in the current text where the deletion should be inserted */
  textPosition: number;
  /** The deleted text to display */
  text: string;
}

/**
 * Result of building the diff application plan
 */
export interface DiffApplicationPlan {
  insertions: InsertionRange[];
  deletions: DeletionInfo[];
}

/**
 * Compute character-level diffs between two text strings.
 * Uses diff-match-patch with semantic cleanup for readable diffs.
 */
export function computeDiff(
  originalText: string,
  modifiedText: string,
): DiffResult {
  const dmp = new DiffMatchPatch();
  const diffResult = dmp.diff_main(originalText, modifiedText);
  dmp.diff_cleanupSemantic(diffResult);

  const chunks: DiffChunk[] = diffResult.map(([operation, text]) => ({
    operation: operation as DiffOperation,
    text,
  }));

  const hasChanges = chunks.some((chunk) => chunk.operation !== 0);

  return { chunks, hasChanges };
}

/**
 * Extract plain text from a TipTap editor.
 * Used to get text content for diff comparison.
 */
export function extractPlainText(editor: Editor): string {
  return editor.state.doc.textContent;
}

// ---------------------------------------------------------------------------
// Editor-escaped Markdown
//
// `@tiptap/markdown` >= 3.22 saves text as CommonMark, not plain text: `& < >`
// become `&amp; &lt; &gt;` and `\ ` * _ [ ] ~` get a backslash (`snake\_case`,
// `\[note\]`); upstream is adding block escapes (`\# not a heading`,
// `1\. not a list`). Mirrors `services/common/editor_markdown.py` — keep the two
// in lockstep. Both are pinned by `__fixtures__/editor_markdown_vectors.json`,
// generated from the real serializer by
// `scripts/editor_markdown/gen_editor_markdown_vectors.cjs`.
// ---------------------------------------------------------------------------

/** CommonMark: `\` before any ASCII punctuation escapes it; before anything else it is literal. */
const ESCAPE_PATTERN = /\\([!-/:-@[-`{-~])/g;
/** Exactly what `@tiptap/core`'s `decodeHtmlEntities` decodes; other entities stay literal. */
const ENTITY_PATTERN = /&(?:lt|gt|quot|amp);/g;
/** One left-to-right scan: an escape consumes the next char, so `\&amp;` is literal `&amp;`. */
const TOKEN_PATTERN = /\\([!-/:-@[-`{-~])|&(?:lt|gt|quot|amp);/g;
/**
 * `shieldEscapes` swaps `\X` for U+E000 + X (U+E021..U+E07E): Private Use Area,
 * BMP so lengths match the Python mirror.
 */
const PLACEHOLDER_OFFSET = 0xe000;
const PLACEHOLDER_PATTERN = /[\ue021-\ue07e]/g;

function decodeEntity(entity: string): string {
  switch (entity) {
    case "&lt;":
      return "<";
    case "&gt;":
      return ">";
    case "&quot;":
      return '"';
    default:
      return "&";
  }
}

/**
 * Stored entry text with editor escapes and entities decoded; Markdown syntax
 * (`**bold**`, `- item`) is left in place. `snake\_case &amp; x` →
 * `snake_case & x`. Not idempotent (`&amp;lt;` → `&lt;` → `<`): decode once.
 */
export function decodeEditorMarkdown(markdown: string): string {
  return markdown.replace(
    TOKEN_PATTERN,
    (token: string, escaped: string | undefined) =>
      escaped ?? decodeEntity(token),
  );
}

/** `decodeEditorMarkdown` output plus where each char came from. */
export interface DecodedMarkdown {
  text: string;
  /**
   * `rawOffsets[i]` is the raw offset where decoded char `i` starts (the `\`
   * of an escape, the `&` of an entity); `rawOffsets[text.length]` is the raw
   * length. Slicing raw by mapped offsets keeps escapes and entities whole.
   */
  rawOffsets: number[];
}

export function decodeEditorMarkdownWithOffsets(
  markdown: string,
): DecodedMarkdown {
  let text = "";
  const rawOffsets: number[] = [];
  let cursor = 0;
  for (const match of markdown.matchAll(TOKEN_PATTERN)) {
    const start = match.index;
    for (let raw = cursor; raw < start; raw++) {
      text += markdown[raw];
      rawOffsets.push(raw);
    }
    text += match[1] ?? decodeEntity(match[0]);
    rawOffsets.push(start);
    cursor = start + match[0].length;
  }
  for (let raw = cursor; raw < markdown.length; raw++) {
    text += markdown[raw];
    rawOffsets.push(raw);
  }
  rawOffsets.push(markdown.length);
  return { text, rawOffsets };
}

/** Swap each `\X` escape for a placeholder no Markdown regex matches. */
function shieldEscapes(markdown: string): string {
  return markdown.replace(ESCAPE_PATTERN, (_escape: string, char: string) =>
    String.fromCharCode(PLACEHOLDER_OFFSET + char.charCodeAt(0)),
  );
}

/** Turn `shieldEscapes` placeholders back into their literal characters. */
function unshieldEscapes(text: string): string {
  return text.replace(PLACEHOLDER_PATTERN, (placeholder: string) =>
    String.fromCharCode(placeholder.charCodeAt(0) - PLACEHOLDER_OFFSET),
  );
}

/** `@tiptap/core` `decodeHtmlEntities`; one pass is the same as `&amp;` last. */
function decodeHtmlEntities(text: string): string {
  return text.replace(ENTITY_PATTERN, decodeEntity);
}

/**
 * Normalize markdown content to plain text for diff comparison.
 *
 * MUST match `normalize_markdown_to_plain_text` in
 * services/record_briefing/text_position_converter.py; both are pinned by
 * `__fixtures__/editor_markdown_vectors.json`.
 *
 * TipTap's `textContent` concatenates all text nodes without paragraph separators.
 * So "Para 1.\n\nPara 2." in markdown becomes "Para 1.Para 2." in textContent.
 *
 * This function normalizes markdown the same way:
 * 1. Shield backslash escapes (`\*`, `\_`, `1\.`) so no regex below reads an escaped literal as syntax
 * 2. Remove empty paragraphs (`&nbsp;` lines) and list/blockquote/hr markers - TipTap extracts only their text
 * 3. Remove markdown formatting syntax (**, *, ~~, etc.) - these become invisible in textContent
 * 4. Remove paragraph breaks (blank lines) and single line breaks - textContent has neither
 * 5. Decode entities (`&amp;` → `&`) and restore shielded escapes, last, so decoded chars are never read as syntax
 *
 * Stored text may be escaped by the editor or not (older LLM output); both come
 * out as the text the editor shows.
 *
 * Note: This uses simple regex patterns which work for TipTapEditor's supported markdown subset.
 * If we expand markdown support or encounter more edge cases, consider using a library like
 * `markdown-to-txt` (https://github.com/ejrbuss/markdown-to-txt) which uses `marked` for
 * proper parsing. The tradeoff is an additional dependency and slightly more overhead.
 */
export function normalizeMarkdownToPlainText(markdownContent: string): string {
  const stripped = shieldEscapes(markdownContent)
    // Remove empty paragraphs, which the editor saves as a lone `&nbsp;` line
    .replace(/^(?:&nbsp;|\u00a0)$/gm, "")

    // IMPORTANT: Process list/blockquote/hr markers FIRST (before formatting markers)
    // This prevents `* item` from being matched by the italic regex `*text*`

    // Remove horizontal rules (---, ***, ___) - these produce no text in TipTap
    // Must be on their own line (possibly with leading/trailing whitespace)
    .replace(/^[ \t]*[-*_]{3,}[ \t]*$/gm, "")
    // Remove blockquote markers at start of lines (> text -> text)
    .replace(/^>\s*/gm, "")
    // Remove unordered list markers at start of lines (- item, * item, + item)
    .replace(/^[-*+]\s+/gm, "")
    // Remove ordered list markers at start of lines (1. item, 2. item, etc.)
    .replace(/^\d+\.\s+/gm, "")

    // Remove bold/italic/strikethrough/underline markers (the text inside is preserved)
    .replace(/\*\*([^*]+)\*\*/g, "$1") // **bold**
    .replace(/\*([^*]+)\*/g, "$1") // *italic*
    .replace(/__([^_]+)__/g, "$1") // __bold__
    .replace(/_([^_]+)_/g, "$1") // _italic_
    .replace(/~~([^~]+)~~/g, "$1") // ~~strikethrough~~
    .replace(/\+\+(.+?)\+\+/g, "$1") // ++underline++
    // Also handle underline HTML tags: <u>text</u> -> text
    .replace(/<u>(.*?)<\/u>/gi, "$1")
    // Remove link syntax, keep text: [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")

    // Strip trailing spaces/tabs before a newline. In markdown, 2+ trailing
    // spaces before a newline are a HARD LINE BREAK, which TipTap renders as a
    // <br> that contributes NO characters to doc.textContent. Removing the
    // newline alone (below) would leave those spaces behind as spurious
    // characters absent from the editor's textContent, so any textToDelete
    // spanning a hard break would fail to match and highlight nothing.
    .replace(/[ \t]+\n/g, "\n")

    // Remove paragraph breaks (double newlines) - paragraphs concatenate in textContent
    .replace(/\n\n+/g, "")
    // Remove single newlines (they become <br> in HTML but not in textContent)
    .replace(/\n/g, "");
  return unshieldEscapes(decodeHtmlEntities(stripped));
}

/**
 * Build a diff application plan from diff chunks.
 * This identifies which ranges need insertion marks and where deletion nodes
 * should be inserted.
 *
 * The positions returned are relative to the current (modified) text,
 * not the TipTap document positions.
 */
export function buildDiffApplicationPlan(
  chunks: DiffChunk[],
): DiffApplicationPlan {
  const insertions: InsertionRange[] = [];
  const deletions: DeletionInfo[] = [];

  let currentTextPos = 0; // Position in current (modified) text

  for (const chunk of chunks) {
    if (chunk.operation === 0) {
      // Unchanged: advance position
      currentTextPos += chunk.text.length;
    } else if (chunk.operation === 1) {
      // Insertion (in modified): mark this range
      const from = currentTextPos;
      const to = currentTextPos + chunk.text.length;
      insertions.push({ textFrom: from, textTo: to });
      currentTextPos += chunk.text.length;
    } else if (chunk.operation === -1) {
      // Deletion (was in original, not in modified):
      // Insert deleted text at current position
      deletions.push({
        textPosition: currentTextPos,
        text: chunk.text,
      });
      // Don't advance currentTextPos (deleted text isn't in current)
    }
  }

  return { insertions, deletions };
}

/**
 * Convert text position to TipTap document position.
 * TipTap positions include structural nodes (doc, paragraphs, etc.)
 *
 * This function walks the document and maps text offset to doc position.
 */
export function textPosToDocPos(editor: Editor, textOffset: number): number {
  const { doc } = editor.state;
  let currentTextOffset = 0;
  let resultPos = -1;

  // Walk through the document to find the position
  doc.descendants((node, pos) => {
    if (resultPos >= 0) return false; // Already found, stop traversal

    if (node.isText) {
      const nodeTextLength = node.text?.length || 0;
      if (currentTextOffset + nodeTextLength > textOffset) {
        // The target offset is within this text node (strictly inside)
        resultPos = pos + (textOffset - currentTextOffset);
        return false;
      }
      currentTextOffset += nodeTextLength;
    }
    return true;
  });

  // If we didn't find it, return end of document
  if (resultPos < 0) {
    resultPos = doc.content.size;
  }

  return resultPos;
}

/**
 * Apply diff visualization to a TipTap editor.
 * This inserts deletion nodes and applies insertion marks.
 *
 * IMPORTANT: The editor should be in read-only mode before calling this.
 *
 * Strategy:
 * 1. First insert all deletion nodes (in reverse order to preserve positions)
 * 2. Then apply insertion marks (recalculating positions to account for inserted nodes)
 */
export function applyDiffToEditor(
  editor: Editor,
  originalContent: string,
): void {
  // Get current text from editor
  const currentText = extractPlainText(editor);

  // Normalize the original markdown to plain text (matching how TipTap's textContent works).
  // This ensures we compare apples to apples - both as plain text without paragraph separators.
  // Without this, markdown newlines would be treated as deletions since they don't appear in textContent.
  const originalText = normalizeMarkdownToPlainText(originalContent);

  // Compute diff between the normalized texts
  const { chunks, hasChanges } = computeDiff(originalText, currentText);

  if (!hasChanges) return;

  // Build application plan
  const { insertions, deletions } = buildDiffApplicationPlan(chunks);

  // Sort deletions by position (descending) to insert from end to start
  // This preserves positions during insertion
  const sortedDeletions = [...deletions].sort(
    (a, b) => b.textPosition - a.textPosition,
  );

  // Step 1: Insert deletion nodes (in reverse order)
  for (const deletion of sortedDeletions) {
    const docPos = textPosToDocPos(editor, deletion.textPosition);
    editor.commands.insertDiffDeletion(deletion.text, docPos);
  }

  // Step 2: Apply insertion marks
  // The textPosToDocPos function walks through TEXT nodes only, skipping deletion nodes.
  // Since deletion nodes are atom nodes (not text nodes), they don't affect text offset counting.
  // However, they DO take up document positions, which textPosToDocPos correctly accounts for
  // by returning the actual doc position where each text offset is found.
  for (const insertion of insertions) {
    const docFrom = textPosToDocPos(editor, insertion.textFrom);
    const docTo = textPosToDocPos(editor, insertion.textTo);

    editor.commands.setDiffInsertion(docFrom, docTo);
  }
}

/**
 * Clear all diff visualization from the editor.
 */
export function clearDiffFromEditor(editor: Editor): void {
  editor
    .chain()
    .command(({ commands }) => {
      commands.unsetAllDiffInsertions();
      return true;
    })
    .command(({ commands }) => {
      commands.removeAllDiffDeletions();
      return true;
    })
    .run();
}
